import React, { useState } from 'react';
import { FaFolderOpen, FaSpinner, FaTimes } from 'react-icons/fa';
import { decodeSlashes } from '../../../utility/formatCustomerName';
import css from './DownloadModal.module.css';

type GroupByOption = 'Stage' | 'FileType' | 'None';

interface DownloadModalProps {
  folderName: string;
  onConfirm: (groupBy: GroupByOption) => void;
  onCancel: () => void;
  isDownloading: boolean;
  initialFolderName: string; 
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  folderName,
  onConfirm,
  onCancel,
  isDownloading,
  initialFolderName
}) => {
  const [minimized, setMinimized] = useState(false);
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOption>('Stage');
  
  // Decode folder names for display
  const decodedFolderName = decodeSlashes(folderName)
    .replace(/REPLACINGSLASHES/g, '/');
  const decodedInitialFolderName = decodeSlashes(initialFolderName)
    .replace(/REPLACINGSLASHES/g, '/');

  const handleConfirm = () => {
    setMinimized(true);
    onConfirm(selectedGroupBy);
  };

  const handleCancel = () => {
    onCancel();
  };

  if (minimized && isDownloading) {
    return (
      <div className={css.minimizedModal}>
        <div className={css.spinnerContainer}>
          <FaSpinner className={css.spinner} />
        </div>
        <div className={css.minimizedText}>
          Downloading {decodedInitialFolderName}...
        </div>
      </div>
    );
  }

  return (
    <div className={css.modalOverlay}>
      <div className={css.modal}>
        <button className={css.closeButton} onClick={handleCancel}>
          <FaTimes />
        </button>
        
        <div className={css.modalHeader}>
          <FaFolderOpen className={css.folderIcon} />
          <h2>Download Folder</h2>
        </div>
        
        <div className={css.modalContent}>
          <p>
            You are about to download <strong>{decodedFolderName}</strong>.
          </p>
          <p>
            This process may take a minute to complete. You can continue to navigate 
            through folders while the download is in progress.
          </p>
          
          <div className={css.groupingOptions}>
            <h3>Organize files by:</h3>
            <div className={css.radioGroup}>
              <label className={css.radioLabel}>
                <input
                  type="radio"
                  name="groupBy"
                  value="Stage"
                  checked={selectedGroupBy === 'Stage'}
                  onChange={(e) => setSelectedGroupBy(e.target.value as GroupByOption)}
                />
                <span>Stage (default)</span>
              </label>
              <label className={css.radioLabel}>
                <input
                  type="radio"
                  name="groupBy"
                  value="FileType"
                  checked={selectedGroupBy === 'FileType'}
                  onChange={(e) => setSelectedGroupBy(e.target.value as GroupByOption)}
                />
                <span>File Type</span>
              </label>
              <label className={css.radioLabel}>
                <input
                  type="radio"
                  name="groupBy"
                  value="None"
                  checked={selectedGroupBy === 'None'}
                  onChange={(e) => setSelectedGroupBy(e.target.value as GroupByOption)}
                />
                <span>All files in one folder</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className={css.modalFooter}>
          <button 
            className={css.cancelButton} 
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button 
            className={css.confirmButton} 
            onClick={handleConfirm}
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;